import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TeamPlanAPI } from '../../api/TeamPlanAPI';
import PageTitle from '../../components/PageTitle';
import { TEAM } from '../../constants/team';
import { PlusIcon } from '../../layout/icons';
import { errorText, MilestoneDialog, TaskDialog } from './PlanDialogs';
import { Avatar, MilestoneIcon } from './parts';
import {
  belongsTo, columnTasks, dueWithin, formatDay, overdue, progress, splitMilestones, STATUSES, timeLeft,
} from './board';

const DONE_SHOWN = 8;

const fmtDoneAt = (iso) =>
  iso ? new Date(iso).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' }) : '';

function MilestoneCard({ milestone, tasks, selected, reached, onSelect, onEdit }) {
  const { done, total, pct } = progress(milestone.id, tasks);
  const left = timeLeft(milestone.dueDate);
  return (
    <div className={'tp-ms' + (selected ? ' is-selected' : '') + (reached ? ' is-reached' : '')}>
      <button type="button" className="tp-ms__main" onClick={onSelect} aria-pressed={selected}
        title={selected ? 'Filter aufheben' : 'Nur die Aufgaben dieses Meilensteins zeigen'}>
        <span className="tp-ms__title"><MilestoneIcon />{milestone.title}</span>
        <span className="tp-ms__due">
          bis {formatDay(milestone.dueDate)} · {reached
            ? <span className="tp-tone tp-tone--done">erreicht</span>
            : <span className={`tp-tone tp-tone--${left.tone}`}>{left.text}</span>}
        </span>
        <span className="tp-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
        <span className="tp-ms__count">{total ? `${done} von ${total} erledigt` : 'Noch keine Aufgaben'}</span>
      </button>
      <button type="button" className="tp-ms__edit" onClick={onEdit} aria-label={`${milestone.title} bearbeiten`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
          strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      </button>
    </div>
  );
}

function TaskCard({ task, milestone, showMilestone, dragging, onOpen, onDragStart, onDragEnd }) {
  const done = task.status === 'DONE';
  const left = !done && timeLeft(task.dueDate);
  const open = () => onOpen(task);
  return (
    <article
      className={'tp-card' + (dragging ? ' is-dragging' : '') + (done ? ' is-done' : '') + (left ? ` tp-card--${left.tone}` : '')}
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      onDragEnd={onDragEnd}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      role="button"
      tabIndex={0}
      aria-label={`${task.title} bearbeiten`}
    >
      <p className="tp-card__title">{task.title}</p>
      {task.description && <p className="tp-card__desc">{task.description}</p>}
      {milestone && showMilestone && <span className="tp-card__ms"><MilestoneIcon />{milestone.title}</span>}
      <div className="tp-card__foot">
        <span className={'tp-person' + (task.assignee ? '' : ' tp-person--none')}>
          <Avatar name={task.assignee} />
          {task.assignee || 'Niemand'}
        </span>
        {done && task.doneAt && <span className="tp-due tp-due--done">erledigt {fmtDoneAt(task.doneAt)}</span>}
        {left && (
          <span className={`tp-due tp-due--${left.tone}`}>
            <span className="tp-due__day">{formatDay(task.dueDate)}</span> · {left.text}
          </span>
        )}
      </div>
    </article>
  );
}

/**
 * The team's tasks as a board: what is open, in progress and done, who does it and by when,
 * under milestones with a deadline and their progress.
 */
export default function TeamPlan() {
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState('');
  const [person, setPerson] = useState('');
  const [milestoneFilter, setMilestoneFilter] = useState(null);
  const [showReached, setShowReached] = useState(false);
  const [showAllDone, setShowAllDone] = useState(false);
  const [taskEditor, setTaskEditor] = useState(null);
  const [milestoneEditor, setMilestoneEditor] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [taskList, milestoneList] = await Promise.all([TeamPlanAPI.tasks(), TeamPlanAPI.milestones()]);
      setTasks(taskList);
      setMilestones(milestoneList);
    } catch (err) {
      setError(errorText(err).replace('Speichern', 'Laden'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { active, reached } = useMemo(() => splitMilestones(milestones, tasks), [milestones, tasks]);
  const milestoneById = useMemo(() => new Map(milestones.map((m) => [m.id, m])), [milestones]);
  const visible = useMemo(() => tasks
    .filter((t) => belongsTo(t, person))
    .filter((t) => milestoneFilter == null || t.milestoneId === milestoneFilter), [tasks, person, milestoneFilter]);
  const summary = useMemo(() => {
    const late = overdue(visible).length;
    return {
      open: visible.filter((t) => t.status === 'OPEN').length,
      inProgress: visible.filter((t) => t.status === 'IN_PROGRESS').length,
      overdue: late,
      // Due this week but not late yet; the late ones are counted on their own.
      week: dueWithin(visible, 7).length - late,
    };
  }, [visible]);

  // ── tasks ─────────────────────────────────────────
  const saveTask = async (payload) => {
    const existing = taskEditor?.task;
    const saved = existing
      ? await TeamPlanAPI.updateTask(existing.id, payload)
      : await TeamPlanAPI.createTask(payload);
    setTasks((all) => existing ? all.map((t) => (t.id === saved.id ? saved : t)) : [...all, saved]);
    setTaskEditor(null);
  };

  const deleteTask = async () => {
    const { id } = taskEditor.task;
    await TeamPlanAPI.deleteTask(id);
    setTasks((all) => all.filter((t) => t.id !== id));
    setTaskEditor(null);
  };

  const newTask = (status = 'OPEN') => setTaskEditor({
    task: null,
    defaults: { status, assignee: person || '', milestoneId: milestoneFilter ?? '' },
  });

  // ── milestones ────────────────────────────────────
  const saveMilestone = async (payload) => {
    const existing = milestoneEditor?.milestone;
    const saved = existing
      ? await TeamPlanAPI.updateMilestone(existing.id, payload)
      : await TeamPlanAPI.createMilestone(payload);
    setMilestones((all) => existing ? all.map((m) => (m.id === saved.id ? saved : m)) : [...all, saved]);
    setMilestoneEditor(null);
  };

  const deleteMilestone = async () => {
    const { id } = milestoneEditor.milestone;
    await TeamPlanAPI.deleteMilestone(id);
    setMilestones((all) => all.filter((m) => m.id !== id));
    setTasks((all) => all.map((t) => (t.milestoneId === id ? { ...t, milestoneId: null } : t)));
    if (milestoneFilter === id) setMilestoneFilter(null);
    setMilestoneEditor(null);
  };

  // ── drag and drop ─────────────────────────────────
  const onDragStart = (e, id) => {
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(id);
  };
  const onDragEnd = () => { setDraggingId(null); setDragOver(null); };
  const onDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOver !== status) setDragOver(status);
  };
  const onDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); };
  const onDrop = async (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const id = Number(e.dataTransfer.getData('text/plain'));
    const task = tasks.find((t) => t.id === id);
    if (!task || task.status === status) return;
    setNotice('');
    setTasks((all) => all.map((t) => (t.id === id ? { ...t, status, doneAt: status === 'DONE' ? new Date().toISOString() : null } : t)));
    try {
      const saved = await TeamPlanAPI.moveTask(id, status);
      setTasks((all) => all.map((t) => (t.id === id ? saved : t)));
    } catch (err) {
      setTasks((all) => all.map((t) => (t.id === id ? task : t)));
      setNotice(errorText(err));
    }
  };

  // ── render ────────────────────────────────────────
  const milestonesShown = showReached ? [...active, ...reached] : active;

  return (
    <div className="page tp">
      <div className="page-header">
        <PageTitle
          title="Aufgaben"
          description="Was im Team zu tun ist, wer es übernimmt und bis wann. Zieh eine Karte in die nächste Spalte, wenn sich ihr Stand ändert."
        />
        <div className="tp-header-actions">
          <button type="button" className="tp-btn" onClick={() => setMilestoneEditor({ milestone: null })}>
            <PlusIcon /> Meilenstein
          </button>
          <button type="button" className="tp-btn tp-btn--primary" onClick={() => newTask()}>
            <PlusIcon /> Neue Aufgabe
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner" role="status" aria-label="Lädt Aufgaben" />
          <span className="muted">Lade Aufgaben …</span>
        </div>
      )}

      {error && (
        <div className="tp-banner tp-banner--error" role="alert">
          <span>{error}</span>
          <button type="button" className="tp-btn" onClick={load}>Erneut versuchen</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <section className="tp-milestones" aria-label="Meilensteine">
            {milestonesShown.map((m) => (
              <MilestoneCard
                key={m.id}
                milestone={m}
                tasks={tasks}
                reached={reached.includes(m)}
                selected={milestoneFilter === m.id}
                onSelect={() => setMilestoneFilter((current) => (current === m.id ? null : m.id))}
                onEdit={() => setMilestoneEditor({ milestone: m })}
              />
            ))}
            <button type="button" className="tp-ms tp-ms--add" onClick={() => setMilestoneEditor({ milestone: null })}>
              <PlusIcon />
              <span>{milestones.length ? 'Meilenstein' : 'Ersten Meilenstein anlegen, z. B. einen Launch mit Deadline'}</span>
            </button>
          </section>
          {reached.length > 0 && (
            <button type="button" className="tp-link" onClick={() => setShowReached((v) => !v)}>
              {showReached ? 'Erreichte Meilensteine ausblenden' : `${reached.length} erreichte${reached.length === 1 ? 'n' : ''} Meilenstein${reached.length === 1 ? '' : 'e'} anzeigen`}
            </button>
          )}

          <div className="tp-toolbar">
            <div className="tp-chips" role="group" aria-label="Nach Person filtern">
              <button type="button" className={'tp-chip' + (!person ? ' is-on' : '')} aria-pressed={!person}
                onClick={() => setPerson('')}>Alle</button>
              {TEAM.map((m) => (
                <button key={m.name} type="button" className={'tp-chip' + (person === m.name ? ' is-on' : '')}
                  aria-pressed={person === m.name} onClick={() => setPerson(person === m.name ? '' : m.name)}>
                  <Avatar name={m.name} size={18} /> {m.name}
                </button>
              ))}
              {milestoneFilter != null && milestoneById.has(milestoneFilter) && (
                <button type="button" className="tp-chip is-on tp-chip--ms" onClick={() => setMilestoneFilter(null)}
                  aria-label="Meilenstein-Filter aufheben">
                  <MilestoneIcon /> {milestoneById.get(milestoneFilter).title}
                  <span aria-hidden="true" className="tp-chip__x">×</span>
                </button>
              )}
            </div>
            <p className="tp-summary">
              {summary.open} offen · {summary.inProgress} in Arbeit
              {summary.overdue > 0 && <> · <span className="tp-tone tp-tone--overdue">{summary.overdue} überfällig</span></>}
              {' · '}{summary.week} in den nächsten 7 Tagen fällig
            </p>
          </div>

          {notice && <div className="tp-banner tp-banner--error" role="alert">{notice}</div>}

          <div className="tp-board">
            {STATUSES.map((col) => {
              const all = columnTasks(visible, col.id);
              const cards = col.id === 'DONE' && !showAllDone ? all.slice(0, DONE_SHOWN) : all;
              return (
                <section
                  key={col.id}
                  className={'tp-col' + (dragOver === col.id ? ' is-over' : '')}
                  style={{ '--col-accent': col.accent }}
                  aria-label={col.label}
                  onDragOver={(e) => onDragOver(e, col.id)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, col.id)}
                >
                  <header className="tp-col__head">
                    <span className="tp-col__dot" aria-hidden="true" />
                    <h2 className="tp-col__title">{col.label}</h2>
                    <span className="tp-col__count">{all.length}</span>
                    {col.id !== 'DONE' && (
                      <button type="button" className="tp-col__add" onClick={() => newTask(col.id)}
                        aria-label={`Neue Aufgabe in ${col.label}`} title="Neue Aufgabe">
                        <PlusIcon />
                      </button>
                    )}
                  </header>
                  <div className="tp-col__cards">
                    {cards.map((t) => (
                      <TaskCard
                        key={t.id}
                        task={t}
                        milestone={milestoneById.get(t.milestoneId)}
                        showMilestone={milestoneFilter == null}
                        dragging={draggingId === t.id}
                        onOpen={(task) => setTaskEditor({ task })}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                      />
                    ))}
                    {all.length === 0 && <p className="tp-col__empty">Nichts hier</p>}
                    {col.id === 'DONE' && all.length > DONE_SHOWN && (
                      <button type="button" className="tp-link tp-link--center" onClick={() => setShowAllDone((v) => !v)}>
                        {showAllDone ? 'Nur die letzten anzeigen' : `Alle ${all.length} anzeigen`}
                      </button>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      {taskEditor && (
        <TaskDialog
          task={taskEditor.task}
          defaults={taskEditor.defaults}
          milestones={[...active, ...reached]}
          onSave={saveTask}
          onDelete={deleteTask}
          onClose={() => setTaskEditor(null)}
        />
      )}
      {milestoneEditor && (
        <MilestoneDialog
          milestone={milestoneEditor.milestone}
          onSave={saveMilestone}
          onDelete={deleteMilestone}
          onClose={() => setMilestoneEditor(null)}
        />
      )}
    </div>
  );
}
